import { useState, useMemo, useRef, useEffect } from 'react'
import { COMBO_BONUS_Y } from '@/utils/comboSlideLayout'
import {
  isSuporteEvolucaoPlan,
  mergeSuporteEvolucaoSlideLayout,
} from '@/utils/suporteEvolucaoSlideLayout'
import type { PropostaItem, Deal, ComboVariation, ComboVariationWithPricing } from '@/types'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { DEFAULT_BONUS_Y, calculateAllVariations } from '@/utils/dealCalculations'
import type { CatalogPlan } from '@/utils/planCatalog'
import { type AvailableItem } from './proposalStudioConstants'
import { normalizeInitialLayout } from './normalizeInitialLayout'
import { hydrateProposalItems } from './hydrateProposalItems'
import { useProposalStudioCatalog } from './useProposalStudioCatalog'
import { useScopeCategoryLayout } from './useScopeCategoryLayout'
import {
  buildSlideOptions,
  computeActivePlanContext,
  computeProposalTotals,
} from './computeActivePlanContext'
import type { StudioActionDeps } from './proposalStudioActionTypes'
import { useProposalStudioCatalogActions } from './useProposalStudioCatalogActions'
import { useProposalStudioItemListActions } from './useProposalStudioItemListActions'
import { useProposalStudioPlanEditActions } from './useProposalStudioPlanEditActions'
import { useProposalStudioPersistActions } from './useProposalStudioPersistActions'

export interface ProposalStudioProps {
  deal: Deal
  initialItems?: PropostaItem[]
  initialLayout?: LayoutConfig
  initialComboVariations?: ComboVariation[]
  onSave?: (items: PropostaItem[], layout: LayoutConfig, comboVariations?: ComboVariation[]) => void
  onClose: () => void
}

export function useProposalStudio({
  deal,
  initialItems = [],
  initialLayout,
  initialComboVariations = [],
  onSave,
  onClose,
}: ProposalStudioProps) {
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([])
  const [catalogPlans, setCatalogPlans] = useState<CatalogPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [viewMode, setViewMode] = useState<'items' | 'preview'>('preview')
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeFormTab, setActiveFormTab] = useState<'details' | 'scope'>('details')
  const [comboVariations, setComboVariations] = useState<ComboVariation[]>(initialComboVariations)
  const [savingAsTemplate, setSavingAsTemplate] = useState(false)
  const scopeLayoutSignatureRef = useRef<string>('')
  const catalogFingerprintRef = useRef<string>('')

  const [layout, setLayout] = useState<LayoutConfig>(() => normalizeInitialLayout(initialLayout))
  const [proposalItems, setProposalItems] = useState<PropostaItem[]>(() =>
    hydrateProposalItems(initialItems),
  )

  const { loadCatalog } = useProposalStudioCatalog(
    setProposalItems,
    setCatalogPlans,
    setAvailableItems,
    setLoading,
    scopeLayoutSignatureRef,
    catalogFingerprintRef,
  )

  const variationsWithPricing: ComboVariationWithPricing[] = useMemo(
    () => calculateAllVariations(comboVariations, proposalItems),
    [comboVariations, proposalItems],
  )

  const slideOptions = useMemo(
    () => buildSlideOptions(comboVariations, proposalItems, variationsWithPricing),
    [comboVariations, proposalItems, variationsWithPricing],
  )

  const { activePlan, activeServiceGroups, showPricing, comboPriceFrom, comboPriceTo } = useMemo(
    () =>
      computeActivePlanContext({
        comboVariations,
        proposalItems,
        variationsWithPricing,
        activeIndex,
        catalogPlans,
      }),
    [comboVariations, proposalItems, variationsWithPricing, activeIndex, catalogPlans],
  )

  useScopeCategoryLayout({
    catalogPlans,
    proposalItems,
    activeIndex,
    comboVariations,
    variationsWithPricing,
    savedLayout: initialLayout,
    scopeLayoutSignatureRef,
    setLayout,
  })

  useEffect(() => {
    if (comboVariations.length === 0 || !showPricing || !activePlan) return
    setLayout((prev) => {
      if (isSuporteEvolucaoPlan(activePlan)) {
        return mergeSuporteEvolucaoSlideLayout(prev)
      }
      const current = prev.bonusY ?? DEFAULT_BONUS_Y
      if (Math.abs(current - COMBO_BONUS_Y) < 0.05) return prev
      return { ...prev, bonusY: COMBO_BONUS_Y }
    })
  }, [comboVariations.length, showPricing, activeIndex, activePlan, setLayout])

  const totals = useMemo(() => computeProposalTotals(proposalItems), [proposalItems])
  const activeCategoryNames = useMemo(
    () => Array.from(activeServiceGroups.keys()),
    [activeServiceGroups],
  )

  const actionDeps: StudioActionDeps = {
    catalogPlans,
    proposalItems,
    activeIndex,
    draggedIndex,
    comboVariations,
    variationsWithPricing,
    layout,
    activePlan,
    setProposalItems,
    setLayout,
    setActiveIndex,
    setDraggedIndex,
    setDragOverIndex,
    setComboVariations,
    setSaving,
    setSavingAsTemplate,
    scopeLayoutSignatureRef,
    loadCatalog,
  }

  const catalogActions = useProposalStudioCatalogActions(actionDeps)
  const listActions = useProposalStudioItemListActions(actionDeps)
  const planActions = useProposalStudioPlanEditActions(actionDeps)
  const persistActions = useProposalStudioPersistActions({
    ...actionDeps,
    deal,
    onSave,
    onClose,
  })

  return {
    deal,
    onClose,
    availableItems,
    catalogPlans,
    loading,
    saving,
    savingAsTemplate,
    draggedIndex,
    dragOverIndex,
    viewMode,
    setViewMode,
    activeIndex,
    setActiveIndex,
    activeFormTab,
    setActiveFormTab,
    comboVariations,
    setComboVariations,
    layout,
    setLayout,
    proposalItems,
    setProposalItems,
    variationsWithPricing,
    slideOptions,
    activePlan,
    activeServiceGroups,
    showPricing,
    comboPriceFrom,
    comboPriceTo,
    activeCategoryNames,
    totals,
    ...catalogActions,
    ...listActions,
    ...planActions,
    ...persistActions,
  }
}
